import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "context/AuthProvider";
import useAxios from "../../../hooks/useAxios";
import Card from "components/ui-elements/Card";
import Row from "components/ui-elements/Row";
import Col from "components/ui-elements/Col";
import CalloutInfo from "components/widgets/CalloutInfo";
import MyBarChart from "components/ui-elements/MyBarChart";

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [resp, error, loading, refetchDashboard] = useAxios({
    method: "GET",
    url: "/dashboard",
    auth: true,
  });

  useEffect(() => {
    if (typeof refetchDashboard === "function") {
      refetchDashboard();
    }
  }, [location.pathname, user?.context_id, user?.groupname, user?.id_user_group]);

  const cards = useMemo(() => {
    const summary = resp?.data?.summary || {};
    if (resp?.data?.dashboard_type === "admin_koni_kabkota") {
      const roleColors = ["primary", "success", "warning", "danger", "info", "secondary"];
      const roleCards = Array.isArray(summary.role_counts)
        ? summary.role_counts.map((item, index) => ({
            label: item?.label || item?.kode || "SDM",
            value: item?.total || 0,
            icon: "fa-list-alt",
            color: roleColors[index % roleColors.length],
          }))
        : [];
      return [
        { label: "Induk Organisasi", value: summary.induk_organisasi || 0, icon: "fa-sitemap", color: "info" },
        { label: "Cabor", value: summary.cabor || 0, icon: "fa-running", color: "success" },
        { label: "Nomor Pertandingan", value: summary.nomor_pertandingan || 0, icon: "fa-list-ol", color: "primary" },
        { label: "Klub Olahraga", value: summary.klub_olahraga || 0, icon: "fa-shield-alt", color: "warning" },
        { label: "Kejuaraan", value: summary.kejuaraan || 0, icon: "fa-flag", color: "danger" },
        { label: "Pengurus", value: summary.pengurus || 0, icon: "fa-users-cog", color: "secondary" },
        ...roleCards,
      ];
    }
    if (resp?.data?.dashboard_type === "admin_cabor") {
      const roleColors = ["primary", "success", "warning", "danger", "info", "secondary"];
      const roleCards = Array.isArray(summary.role_counts)
        ? summary.role_counts.map((item, index) => ({
            label: item?.label || item?.kode || "SDM",
            value: item?.total || 0,
            icon: "fa-list-alt",
            color: roleColors[index % roleColors.length],
          }))
        : [];
      return [
        { label: "Cabor", value: summary.cabor || 0, icon: "fa-running", color: "info" },
        { label: "Nomor Pertandingan", value: summary.nomor_pertandingan || 0, icon: "fa-list-ol", color: "success" },
        { label: "Klub Olahraga", value: summary.klub_olahraga || 0, icon: "fa-shield-alt", color: "primary" },
        { label: "Pengurus", value: summary.pengurus || 0, icon: "fa-users-cog", color: "warning" },
        ...roleCards,
      ];
    }
    if (resp?.data?.dashboard_type === "admin_klub") {
      const roleColors = ["primary", "success", "warning", "danger", "info", "secondary"];
      const roleCards = Array.isArray(summary.role_counts)
        ? summary.role_counts.map((item, index) => ({
            label: item?.label || item?.kode || "SDM",
            value: item?.total || 0,
            icon: "fa-list-alt",
            color: roleColors[index % roleColors.length],
          }))
        : [];
      return [
        { label: "Cabor", value: summary.cabor || 0, icon: "fa-running", color: "info" },
        { label: "Nomor Pertandingan", value: summary.nomor_pertandingan || 0, icon: "fa-list-ol", color: "success" },
        { label: "Pengurus", value: summary.pengurus || 0, icon: "fa-users-cog", color: "warning" },
        ...roleCards,
      ];
    }
    if (resp?.data?.dashboard_type === "admin_kejuaraan") {
      return [
        { label: "Kontingen", value: summary.kontingen || 0, icon: "fa-flag", color: "warning", size: "4" },
        { label: "Cabor", value: summary.cabor || 0, icon: "fa-running", color: "info", size: "4" },
        { label: "Nomor Pertandingan", value: summary.nomor_pertandingan || 0, icon: "fa-list-ol", color: "success", size: "4" },
        { label: "Target Emas", value: summary.target_emas || 0, icon: "fa-medal", color: "warning", size: "2" },
        { label: "Hasil Emas", value: summary.hasil_emas || 0, icon: "fa-trophy", color: "warning", size: "2" },
        { label: "Target Perak", value: summary.target_perak || 0, icon: "fa-medal", color: "secondary", size: "2" },
        { label: "Hasil Perak", value: summary.hasil_perak || 0, icon: "fa-trophy", color: "secondary", size: "2" },
        { label: "Target Perunggu", value: summary.target_perunggu || 0, icon: "fa-medal", color: "danger", size: "2" },
        { label: "Hasil Perunggu", value: summary.hasil_perunggu || 0, icon: "fa-trophy", color: "danger", size: "2" },
      ];
    }
    if (resp?.data?.dashboard_type === "personal") {
      return [
        { label: "Cabor", value: summary.cabor || 0, icon: "fa-running", color: "info" },
        { label: "Induk Organisasi", value: summary.induk_organisasi || 0, icon: "fa-sitemap", color: "success" },
        { label: "Klub Olahraga", value: summary.klub_olahraga || 0, icon: "fa-shield-alt", color: "warning" },
        { label: "Peran Keolahragaan", value: summary.peran_keolahragaan_aktif || 0, icon: "fa-user-tag", color: "primary" },
      ];
    }
    return [
      { label: "Cabor Aktif", value: summary.cabor || 0, icon: "fa-running", color: "info" },
      { label: "Organisasi Aktif", value: summary.organisasi || 0, icon: "fa-sitemap", color: "success" },
      { label: "Personal Aktif", value: summary.personal || 0, icon: "fa-users", color: "warning" },
      { label: "Keanggotaan Aktif", value: summary.keanggotaan_aktif || 0, icon: "fa-id-card", color: "primary" },
    ];
  }, [resp]);
  const summary = resp?.data?.summary || {};
  const isAdminKejuaraan = resp?.data?.dashboard_type === "admin_kejuaraan";
  const medalChartRows = Array.isArray(summary.chart_target_hasil_medali) ? summary.chart_target_hasil_medali : [];
  const medalChartLabels = medalChartRows.map((item) => item?.cabor_name || "-");
  const medalChartData = [
    {
      label: "Target Emas",
      data: medalChartRows.map((item) => Number(item?.target_emas || 0)),
      backgroundColor: "rgba(255, 193, 7, 0.72)",
      borderColor: "rgba(255, 193, 7, 1)",
      borderWidth: 1,
    },
    {
      label: "Hasil Emas",
      data: medalChartRows.map((item) => Number(item?.hasil_emas || 0)),
      backgroundColor: "rgba(40, 167, 69, 0.72)",
      borderColor: "rgba(40, 167, 69, 1)",
      borderWidth: 1,
    },
  ];
  const medalChartHeight = Math.max(300, medalChartRows.length * 28);
  const medalChartKey = medalChartRows
    .map((item) => `${item?.cabor_name || ""}:${item?.target_emas || 0}:${item?.hasil_emas || 0}`)
    .join("|");

  if (loading) {
    return <Card title="Dashboard">Memuat ringkasan dashboard...</Card>;
  }

  if (error) {
    return <Card title="Dashboard">Dashboard belum dapat dimuat: {error}</Card>;
  }

  return (
    <div>
      <CalloutInfo
        title={resp?.data?.welcome || "Dashboard SIMAKOR"}
      >
        <p className="mb-1">
          Melalui sistem ini pengelolaan KONI dan data keolahragaan menjadi lebih efisien, mudah, cepat, dan paperless.
        </p>
      </CalloutInfo>
      <Card title="Ringkasan Data">
        <Row>
          {cards.map((card) => (
            <Col size={card.size || "2"} key={card.label}>
              <div className={`small-box bg-${card.color}`}>
                <div className="inner">
                  <h3>{card.value}</h3>
                  <p>{card.label}</p>
                </div>
                <div className="icon">
                  <i className={`fas ${card.icon}`} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>
      {isAdminKejuaraan && medalChartRows.length > 0 ? (
        <MyBarChart
          key={medalChartKey}
          label="Evaluasi Perolehan Medali Emas"
          labels={medalChartLabels}
          data={medalChartData}
          className="primary"
          scaleType="linear"
          indexAxis="y"
          height={medalChartHeight}
          maxHeight={720}
          overflowY="auto"
        />
      ) : null}
    </div>
  );
}
